import 'react-loading-skeleton/dist/skeleton.css';

import React, { Suspense, useContext, useEffect, useRef, useState } from 'react';
import { useDelaySwitch } from '@dsg-tech/homefield-react';
import {
  aosSelector,
  apiEndpointsSelector,
  breakpointSelector,
  getFeatureFlagByNameSelector,
  storeIdentifierNameSelector,
} from 'store/selectors/commonSelector';
import { showPreSaytDrawerSelector, showSaytDrawerSelector } from 'store/selectors/saytSelector';

import { StoreIdentifierName, TrendingSearch } from 'types';
import { headerTaxoSelector } from 'store/selectors/taxonomySelector';
import { useAppSelector } from 'store/store';
import useTrendingSearchDataFromWindow from 'hooks/useTrendingSearchDataFromWindow';
import useHomr from 'hooks/useHomr';
import usePARTY from 'hooks/usePARTY';
import useSAYT from 'hooks/useSAYT';
import ErrorBoundaryWithLogger from 'common/ErrorBoundary/ErrorBoundary';
import { WindowContext } from '../../../utility/windowContext';
import TrendingSearches from './TrendingSearches/TrendingSearches';
import AssociateOrderingSystem from './AOS/AOS';
import DesktopNav from './Navigation/DesktopNav/DesktopNav';
import ConsolidatedNav from './ConsolidatedNav/ConsolidatedNav';
import FooterSkipLink from './SkipLinks/FooterSkipLink';
import MainContentSkipLink from './SkipLinks/MainContentSkipLink';
import MainHeader from './MainHeader/MainHeader';
import MobileNav from './Navigation/MobileNav/MobileNav';
import Modal from '../../common/Modal/Modal';
import MyAccountDrawer from './MyAccountDrawer/MyAccountDrawer';
import ProgressiveHeader from './ProgressiveHeader/ProgressiveHeader';
import TopNavBar from './NavBar/TopNavBar';
import Ticker, { TickerFallback } from '../../common/Ticker/Ticker';
import './Header.scss';

declare global {
  interface Window {
    authFunctions: any;
    ceopsStatus: any;
    _dsgTag: any;
  }
}

export default function Header() {
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const taxoContent = useAppSelector(headerTaxoSelector);
  const apiObject = useAppSelector(apiEndpointsSelector);
  const aos = useAppSelector(aosSelector);
  const viewPortBreakpoint = useAppSelector(breakpointSelector);
  const headerRef = useRef(null);
  const [hasTopNavViewEventBeenCalled, setHasTopNavViewEventBeenCalled] = useState<boolean>(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [certonaRecommendations, setCertonaRecommendations] = useState(null);
  const progressiveHeaderEnabled = useAppSelector(
    getFeatureFlagByNameSelector('progressiveHeader'),
  );
  const hideUtilityBar = useAppSelector(getFeatureFlagByNameSelector('hideUtilityBar'));
  const [nikeConnected] = usePARTY();
  const showSaytDrawer = useAppSelector(showSaytDrawerSelector);
  const showPreSaytDrawer = useAppSelector(showPreSaytDrawerSelector);
  useSAYT();
  const { storeByNumberEndpoint } = apiObject;
  const progressiveHeaderHomrData = useHomr(
    'progressive-header',
    'default',
    storeIdentifierName as StoreIdentifierName,
  );
  const progressiveHeaderApiResponse = progressiveHeaderHomrData
    ? progressiveHeaderHomrData[0]
    : null;
  const { state, neutral, switchState } = useDelaySwitch(false, 250);
  const { trendingSearchWindowData } = useTrendingSearchDataFromWindow();
  const {
    state: myAccountDrawerState,
    neutral: myAccountDrawerNeutral,
    switchState: myAccountDrawerSwitchState,
  } = useDelaySwitch(false, 250);
  const disableTrendingSearchBarOnHomePage = useAppSelector(
    getFeatureFlagByNameSelector('disableTrendingSearchBarOnHomePage'),
  );
  const trendingSearchBarEnabled = useAppSelector(
    getFeatureFlagByNameSelector('trendingSearchBarEnabled'),
  );
  const allCategoriesFlag = useAppSelector(getFeatureFlagByNameSelector('allCategories'));
  const l0LinksFlag = useAppSelector(getFeatureFlagByNameSelector('consolidatedNavL0Links'));
  const trendingSearchInConsolidatedFlag = useAppSelector(
    getFeatureFlagByNameSelector('trendingSearchInConsolidatedNav'),
  );
  const win = useContext(WindowContext);
  // Uncomment this line to test the header on local
  // const isHomepage: boolean =
  //   typeof window !== 'undefined' && window.location && window.location.pathname === '/resheader';
  const isHomepage: boolean = win && window.location ? window.location.pathname === '/' : false;
  const [trendingSearchHomrData, setTrendingSearchHomrData] = useState<TrendingSearch | null>(null);
  const showTrendingSearch: boolean =
    !disableTrendingSearchBarOnHomePage &&
    (isHomepage || trendingSearchBarEnabled) &&
    (!!trendingSearchWindowData || !!trendingSearchHomrData);

  // Analytics event: Fires only once per page load, when mobile or desktop header navigation is opened
  const viewTopNavAnalyticsEventHandler = (additionalData = {}) => {
    if (!hasTopNavViewEventBeenCalled) {
      const evt = new CustomEvent('TopNavView', {
        detail: additionalData,
      });
      document.dispatchEvent(evt);
      setHasTopNavViewEventBeenCalled(true);
    }
  };

  const mobileMenuToggleButtonEventHandler = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  useEffect(() => {
    window.headerFunctions = {
      ...(window.headerFunctions && { ...window.headerFunctions }),
      updateSelectedStore: () => {},
    };

    // eslint-disable-next-line no-underscore-dangle
    window._dsgTag?.FunctionRepository?.AddCertonaResponseHandler((response) => {
      setCertonaRecommendations(
        response.resonance.schemes.find((scheme) => scheme.scheme === 'searchbox_rr'),
      );
    });
  }, []);

  useEffect(() => {
    window.onpageshow = (event) => {
      if (event.persisted) {
        const evt = new CustomEvent('updateFavoritesStoreData', { detail: '' });
        window.dispatchEvent(evt);
      }
    };
  }, []);

  useEffect(() => {
    if (showMobileMenu && (showSaytDrawer || showPreSaytDrawer)) {
      setShowMobileMenu(false);
    }
  }, [showSaytDrawer, showPreSaytDrawer]);

  useEffect(() => {
    const trendingSearchesHomr: TrendingSearch | undefined = taxoContent.trendingSearches;
    if (trendingSearchesHomr) {
      setTrendingSearchHomrData(trendingSearchesHomr[0]);
    }
  }, [taxoContent.trendingSearches]);

  return (
    <div role="banner" className="dsg-header-2021" data-testid="header">
      {aos ? (
        <ErrorBoundaryWithLogger componentId="Header-AOS">
          <AssociateOrderingSystem />
        </ErrorBoundaryWithLogger>
      ) : null}
      <ErrorBoundaryWithLogger componentId="Header-MainContentSkipLink">
        <MainContentSkipLink />
      </ErrorBoundaryWithLogger>
      <ErrorBoundaryWithLogger componentId="Header-FooterSkipLink">
        <FooterSkipLink />
      </ErrorBoundaryWithLogger>
      <ErrorBoundaryWithLogger componentId="Header-Ticker" fallback={<TickerFallback />}>
        <Suspense>
          <Ticker storeIdentifierName={storeIdentifierName as StoreIdentifierName} />
        </Suspense>
      </ErrorBoundaryWithLogger>
      {!hideUtilityBar && viewPortBreakpoint !== 'mobile' && aos === false && (
        <ErrorBoundaryWithLogger componentId="Header-TopNavBar">
          <TopNavBar
            navBarCtas={taxoContent.headerNavBar}
            topMessageData={taxoContent.cta}
            storeIdentifierName={storeIdentifierName as StoreIdentifierName}
            showTopMessage={!!taxoContent.cta}
            nikeConnected={nikeConnected}
          />
        </ErrorBoundaryWithLogger>
      )}
      <ErrorBoundaryWithLogger componentId="Header-MainHeader">
        <MainHeader
          certonaRecommendations={certonaRecommendations}
          switchState={switchState}
          myAccountDrawerSwitchState={myAccountDrawerSwitchState}
          trendingSearchWindowData={trendingSearchWindowData}
          trendingSearchHomrData={trendingSearchHomrData}
          showMobileMenu={showMobileMenu}
          setShowMobileMenu={setShowMobileMenu}
          hasTopNavViewEventBeenCalled={hasTopNavViewEventBeenCalled}
          setHasTopNavViewEventBeenCalled={setHasTopNavViewEventBeenCalled}
          showTrendingSearch={showTrendingSearch}
        />
      </ErrorBoundaryWithLogger>
      <ErrorBoundaryWithLogger componentId="Header-MobileNav">
        <MobileNav
          mobileNavData={taxoContent.Taxonomy2}
          navBarData={taxoContent.headerNavBar}
          showMobileMenu={showMobileMenu}
          setShowMobileMenu={setShowMobileMenu}
          storeByNumberEndpoint={storeByNumberEndpoint}
          storeIdentifierName={storeIdentifierName}
          showMobileMenuClickHandler={mobileMenuToggleButtonEventHandler}
          viewTopNavAnalyticsEvent={viewTopNavAnalyticsEventHandler}
          viewPortBreakpoint={viewPortBreakpoint}
          nikeConnected={nikeConnected}
        />
      </ErrorBoundaryWithLogger>
      {showTrendingSearch && (
        <ErrorBoundaryWithLogger componentId="Header-TrendingSearches">
          <TrendingSearches
            content={
              trendingSearchWindowData && trendingSearchWindowData !== null
                ? trendingSearchWindowData
                : trendingSearchHomrData
            }
            viewPortBreakpoint={viewPortBreakpoint}
          />
        </ErrorBoundaryWithLogger>
      )}
      {(allCategoriesFlag || l0LinksFlag || trendingSearchInConsolidatedFlag) && (
        <ErrorBoundaryWithLogger componentId="Header-ConsolidatedNav">
          <ConsolidatedNav
            content={
              trendingSearchWindowData && trendingSearchWindowData !== null
                ? trendingSearchWindowData
                : trendingSearchHomrData
            }
            storeIdentifierName={storeIdentifierName}
            aos={aos}
            setShowDrawer={() => switchState()}
            navBarData={taxoContent.Taxonomy2}
            nikeConnected={nikeConnected}
            viewTopNavAnalyticsEventHandler={viewTopNavAnalyticsEventHandler}
          />
        </ErrorBoundaryWithLogger>
      )}
      {!allCategoriesFlag &&
        !l0LinksFlag &&
        !trendingSearchInConsolidatedFlag &&
        !progressiveHeaderEnabled && (
          <ErrorBoundaryWithLogger componentId="Header-DesktopNav">
            <DesktopNav
              storeIdentifierName={storeIdentifierName}
              desktopNavData={taxoContent.Taxonomy2}
              viewTopNavAnalyticsEvent={viewTopNavAnalyticsEventHandler}
              viewPortBreakpoint={viewPortBreakpoint}
            />
          </ErrorBoundaryWithLogger>
        )}
      <ErrorBoundaryWithLogger componentId="Header-Modal">
        <Modal />
      </ErrorBoundaryWithLogger>

      {state && (
        <ErrorBoundaryWithLogger componentId="Header-ProgressiveHeader">
          <ProgressiveHeader
            headerRef={headerRef.current}
            showDrawer={state}
            setShowDrawer={() => switchState()}
            closing={neutral}
            storeIdentifierName={storeIdentifierName}
            navBarData={progressiveHeaderApiResponse?.content}
          />
        </ErrorBoundaryWithLogger>
      )}
      <ErrorBoundaryWithLogger componentId="Header-MyAccountDrawer">
        <MyAccountDrawer
          headerRef={headerRef.current}
          showDrawer={myAccountDrawerState}
          setShowDrawer={() => myAccountDrawerSwitchState()}
          closing={myAccountDrawerNeutral}
          viewPortBreakpoint={viewPortBreakpoint}
        />
      </ErrorBoundaryWithLogger>
      {/* <div className="noPLP" /> */}
    </div>
  );
}
